/* import __COLOCATED_TEMPLATE__ from './project-details.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { EMBER_ROUTE_NAMES } from "sh-www/constants/routes";
import {
  PROJECT_DETAILS__APPLICATION_TIMEOUTS,
  PROJECT_DETAILS__SUB_NAV_TITLES,
  PROJECT_DETAILS__APPLICATION_EXPIRATION_STATUSES_ALLOWED
} from "sh-www/constants/routes/project-details";
import { ASSIGNMENT } from "sh-www/models/assignment";
import { APPLICATION } from 'sh-www/models/application';
import { htmlSafe } from '@ember/template';

const CLASS_NAME = 'SH-c-project-details';

class ProjectDetails extends Component {
  @service('error-tracker') errorTracker;
  @service media;
  @service moment;
  @service router;
  @service session;

  @tracked showFullProjectInfo = false;

  get className() {
    return CLASS_NAME;
  }

  get applicationTitle() {
    return htmlSafe(this.args.application.title);
  }

  get assignmentTitle() {
    // determine whether to use memorialized version
    return htmlSafe(this.args.application.applicationDetails?.title ?? this.args.application.assignment.title);
  }

  get communicationExpireNotificationText() {
    return htmlSafe(this.args.communicationExpireNotificationText);
  }

  get brandOrCompanyName() {
    const { brand, company } = this.args.application;
    return htmlSafe(brand?.displayedName ?? company.name);
  }

  get pitchHeadline() {
    return htmlSafe(this.args.application.pitch.headline);
  }

  get projectNavItems() {
    const { currentUser } = this.session;

    const isNavActive = (navTitleToCheck) => navTitleToCheck === this.args.currentSubNavState;
    const navItems = [
      {
        title: PROJECT_DETAILS__SUB_NAV_TITLES.ACTIVITY,
        isActive: () => isNavActive(PROJECT_DETAILS__SUB_NAV_TITLES.ACTIVITY)
      },
      {
        title: PROJECT_DETAILS__SUB_NAV_TITLES.DETAILS,
        isActive: () => isNavActive(PROJECT_DETAILS__SUB_NAV_TITLES.DETAILS),
      },
      {
        title: PROJECT_DETAILS__SUB_NAV_TITLES.DELIVERABLES,
        isActive: () => isNavActive(PROJECT_DETAILS__SUB_NAV_TITLES.DELIVERABLES),
      },
    ];

    if (this.args.application.pitch?.id) {
      navItems.push({
        title: PROJECT_DETAILS__SUB_NAV_TITLES.PITCH,
        isActive: () => isNavActive(PROJECT_DETAILS__SUB_NAV_TITLES.PITCH)
      });
    }

    if (!currentUser.isSeller || this.applicationReachedActive) {
      navItems.push({
        title: PROJECT_DETAILS__SUB_NAV_TITLES.PAYMENT,
        isActive: () => isNavActive(PROJECT_DETAILS__SUB_NAV_TITLES.PAYMENT),
      });
    }

    navItems.push({
      title: PROJECT_DETAILS__SUB_NAV_TITLES.FILES,
      isActive: () => isNavActive(PROJECT_DETAILS__SUB_NAV_TITLES.FILES),
    });

    return navItems;
  }

  get applicationReachedActive() {
    const { status } = this.args.application;
    return APPLICATION.SIMPLE_STATUS_MAPPING[APPLICATION.SIMPLE_STATUS.REACHED_ACTIVE].includes(status);
  }

  get sellerMarkedCompleteDaysAgo() {
    if (!this.args.sellerMarkedCompleteDate) {
      this.errorTracker.error('We do not have the date when seller marked complete', this);
      return '1 day ago';
    }

    return this.moment.moment(this.args.sellerMarkedCompleteDate).fromNow();
  }

  get showActivity() {
    return this.args.currentSubNavState === PROJECT_DETAILS__SUB_NAV_TITLES.ACTIVITY;
  }

  get showDetails() {
    return this.args.currentSubNavState === PROJECT_DETAILS__SUB_NAV_TITLES.DETAILS;
  }

  get showPitch() {
    return this.args.currentSubNavState === PROJECT_DETAILS__SUB_NAV_TITLES.PITCH;
  }

  get showPayment() {
    return this.args.currentSubNavState === PROJECT_DETAILS__SUB_NAV_TITLES.PAYMENT;
  }

  get showFiles() {
    return this.args.currentSubNavState === PROJECT_DETAILS__SUB_NAV_TITLES.FILES;
  }

  get showDeliverables() {
    return this.args.currentSubNavState === PROJECT_DETAILS__SUB_NAV_TITLES.DELIVERABLES;
  }

  get showInitialDetailsLoad() {
    const { shInvoices, shPayments, shInvoiceLineItems, shPaymentLineItems } = this.args.application;
    return this.args.loadPaymentData.isRunning
      && !shInvoices.firstObject && !shInvoiceLineItems.firstObject
      && !shPayments.firstObject && !shPaymentLineItems.firstObject;
  }

  get companyImage() {
    return this.args.application.company.image?.transforms.profile128 ?? '';
  }

  get sellerName() {
    const { seller, company } = this.args.application;
    const { currentUser } = this.session;

    return currentUser.isBuyer && company.isNeedsCreditCard
      ? seller.fullNameSingleLastDigit
      : seller.fullName;
  }


  get isFreelancerBlocked() {
    return this.args.application.seller.isBlockedBy || this.args.application.seller.isBlocked;
  }


  get canShowExpirationTime() {
    const {
      expiresAt,
      status,
      direct,
      isExpiredTimeWithinOneDay
    } = this.args.application;

    if (!this.session.currentUser.isBuyer || !expiresAt) {
      return false;
    }

    return PROJECT_DETAILS__APPLICATION_EXPIRATION_STATUSES_ALLOWED.includes(status) && ((direct && isExpiredTimeWithinOneDay) || !direct)
  }


  get countdownTimerExpirationText() {
    if (this.args.application.direct) {
      return `Projects without an accepted offer are automatically declined after ${PROJECT_DETAILS__APPLICATION_TIMEOUTS.DIRECT} days of no communication.`;
    }

    return this.args.application.assignment.type === ASSIGNMENT.TYPES.STANDARD ?
      `Applications are automatically declined after ${PROJECT_DETAILS__APPLICATION_TIMEOUTS.HIRE} days of no communication.` :
      `Pitches are automatically declined after ${PROJECT_DETAILS__APPLICATION_TIMEOUTS.PITCH} days of no communication.`;
  }

  get disableActionButtons() {
    const { incomingErrors, loadPitchData, loadPaymentData, isLoading } = this.args;
    const hasError = Boolean(Object.keys(incomingErrors).length || loadPitchData.last?.isError || loadPaymentData.last?.isError);
    return isLoading || hasError;
  }

  get errorsToDisplay() {
    const { incomingErrors, loadPitchData, loadPaymentData } = this.args;
    const showLoadError = Boolean(loadPitchData.last?.isError || loadPaymentData.last?.isError);
    const incoming = Object.values(incomingErrors);
    return [
      ...incoming,
      ...(showLoadError ? ['There was a problem loading project data. Please reload page and try again.'] : []),
    ];
  }

  @action
  onProjectNavItemClicked(subNavTitle) {
    const transitionTo = (routeName) => {
      const transition = this.router.transitionTo(routeName, this.args.application);
      transition.data.preserveScrollPosition = true;
    };

    switch (subNavTitle) {
      case PROJECT_DETAILS__SUB_NAV_TITLES.ACTIVITY:
        this.args.updateSubNavState(PROJECT_DETAILS__SUB_NAV_TITLES.ACTIVITY);
        transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS__ACTIVITY);
        break;
      case PROJECT_DETAILS__SUB_NAV_TITLES.DETAILS:
        this.args.updateSubNavState(PROJECT_DETAILS__SUB_NAV_TITLES.DETAILS);
        transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS__DETAILS);
        break;
      case PROJECT_DETAILS__SUB_NAV_TITLES.PAYMENT:
        this.args.updateSubNavState(PROJECT_DETAILS__SUB_NAV_TITLES.PAYMENT);
        transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS__PAYMENT);
        break;
      case PROJECT_DETAILS__SUB_NAV_TITLES.PITCH:
        this.args.updateSubNavState(PROJECT_DETAILS__SUB_NAV_TITLES.PITCH);
        transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS__PITCH);
        break;
      case PROJECT_DETAILS__SUB_NAV_TITLES.FILES:
        this.args.updateSubNavState(PROJECT_DETAILS__SUB_NAV_TITLES.FILES);
        transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS__FILES);
        break;
      case PROJECT_DETAILS__SUB_NAV_TITLES.DELIVERABLES:
        this.args.updateSubNavState(PROJECT_DETAILS__SUB_NAV_TITLES.DELIVERABLES);
        transitionTo(EMBER_ROUTE_NAMES.PROJECT__DETAILS__DELIVERABLES);
        break;
    }
  }

  @action
  onToggleShowFullProjectInfo(showFullProjectInfo) {
    this.showFullProjectInfo = showFullProjectInfo;
  }
}

export default ProjectDetails;
